import {css} from "@emotion/react";
import styled from "@emotion/styled";
import {Skp, Location, Building} from "api/quote";
import {Box} from "components/Box";
import {GridContainer, GridItem} from "components/Grid";
import IndentWrapper from "components/IndentWrapper";
import FormTitle from "pages/RateQuoteBind/components/FormTitle";
import {useMemo} from "react";
import {formatAmount} from "utils/format";
import ActionButton from "../../ActionButton";
import Field from "../../Field";
import {
  causeOfLoss,
  coinsurancePercents,
  constructionTypes,
  deductables,
} from "../../fieldValues";

const LocationDiv = styled.div(
  css`
    padding: 1%;
    border: 1px solid lightBlue;
    margin: 1%;
  `
);
const scheduledDeductables = ["250", "500", "1000", "2500"];
const instalationFloaterLimits = ["5000", "7500", "10000", "25000", "50000"];
function SkpPersonalPropertyForm({
  locationId,
  buildings,
}: {
  locationId: string;
  buildings: Building[];
}) {
  const skpPersonalProperty = useMemo(
    () => buildings.filter((building) => building.skp_personal_property),
    [buildings]
  );

  return (
    <>
      {skpPersonalProperty.map((building, index) => (
        <Box key={index} py={16}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <FormTitle.SubHeader>
              Personal Property {index + 1}
            </FormTitle.SubHeader>
            <ActionButton.Delete
              objectName="building"
              objectId={building.id}
              background
            >
              Delete Personal Property {index + 1}
            </ActionButton.Delete>
          </Box>

          <GridContainer rowGap={24} columnGap={16}>
            <GridItem growXXS={12} growMD={6}>
              <Field.DropDown
                object={building}
                options={constructionTypes.map((item) => ({
                  title: item.label,
                  value: item.value,
                }))}
                field={"construction_type"}
                label="Construction type"
              />
            </GridItem>
            <GridItem growXXS={12} growMD={6}>
              <Field.TextInput
                object={building.skp_personal_property!}
                field={"amount_of_insurance"}
                label="Coverage Amount"
                isFormattedNumber
              />
            </GridItem>
            <GridItem growXXS={12} growMD={6}>
              <Field.DropDown
                object={building.skp_personal_property!}
                options={causeOfLoss.map((item) => ({
                  title: item.label,
                  value: item.value,
                }))}
                field={"cause_of_loss"}
                label="Cause of Loss"
              />
            </GridItem>
            <GridItem growXXS={12} growMD={6}>
              <Field.DropDown
                object={building.skp_personal_property!}
                options={coinsurancePercents.map((item) => ({
                  title: item.label,
                  value: item.value,
                }))}
                field={"coinsurance_percent"}
                label="Coinsurance"
              />
            </GridItem>
            <GridItem growXXS={12} growMD={6}>
              <Field.DropDown
                options={deductables.map((item) => ({
                  title: formatAmount(item),
                  value: item,
                }))}
                object={building.skp_personal_property!}
                field={"deductible"}
                label="Deductible"
              />
            </GridItem>
            <GridItem growXXS={12} growMD={6}>
              <Field.TextInput
                type="number"
                object={building}
                field={"year_built"}
                label="Year built"
              />
            </GridItem>
            <GridItem growXXS={12} growMD={6}>
              <Field.TextInput
                type="number"
                object={building}
                field={"number_of_stories"}
                label="Number of Stories"
              />
            </GridItem>
            <GridItem growXXS={12} growMD={6}>
              <Field.TextInput
                object={building}
                field={"total_floor_area"}
                label="Square Footage"
                isFormattedNumber
              />
            </GridItem>
          </GridContainer>
          <GridContainer rowGap={24} columnGap={16}>
            <GridItem growXXS={12} growMD={12}></GridItem>
          </GridContainer>
        </Box>
      ))}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={20}
        my={20}
      >
        <Box></Box>
        <ActionButton.Add
          objectName="skp_personal_property"
          params={{location_id: locationId}}
          background
        >
          Personal Property Coverage
        </ActionButton.Add>
      </Box>
    </>
  );
}
function SkpCoverageForm({
  skp,
  locations,
  quoteId,
}: {
  skp: Skp;
  locations: Location[];
  quoteId: string;
}) {
  return (
    <>
      {!skp.cim_contractors_tool_equipment && (
        <ActionButton.Add
          objectName="cim_contractors_tool_equipment"
          background
        >
          Contractors Tools and Equipment
        </ActionButton.Add>
      )}
      {skp.cim_contractors_tool_equipment && (
        <>
          <LocationDiv>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <FormTitle.SubHeader>
                Contractor Tools and Equipment
              </FormTitle.SubHeader>
              <ActionButton.Delete
                objectName="cim_contractors_tool_equipment"
                objectId={skp.cim_contractors_tool_equipment.id}
                background
              >
                Remove Contractors Tools and Equipment
              </ActionButton.Delete>
            </Box>
            <Field.CheckBox
              label="Unscheduled"
              object={skp.cim_contractors_tool_equipment}
              field={"unscheduled"}
            />
            {skp.cim_contractors_tool_equipment.unscheduled && (
              <LocationDiv>
                <Field.DropDown
                  options={deductables.map((item) => ({
                    title: formatAmount(item),
                    value: parseInt(item),
                  }))}
                  object={skp.cim_contractors_tool_equipment}
                  field={"unscheduled_limit"}
                  label="Unscheduled Limit"
                />
              </LocationDiv>
            )}
            <Field.CheckBox
              label="Scheduled"
              object={skp.cim_contractors_tool_equipment}
              field={"scheduled"}
            />
            {skp.cim_contractors_tool_equipment.scheduled && (
              <LocationDiv>
                <Field.DropDown
                  options={scheduledDeductables.map((item) => ({
                    title: formatAmount(item),
                    value: parseInt(item),
                  }))}
                  object={skp.cim_contractors_tool_equipment}
                  field={"scheduled_deductible"}
                  label="Scheduled Deductible"
                />
                {skp.cim_contractors_tool_equipment.tool_equipments.map(
                  (tool) => (
                    <LocationDiv>
                      <GridContainer rowGap={24} columnGap={16}>
                        <Field.TextInput
                          object={tool}
                          field={"make"}
                          label="Make"
                          wrapperProps={{
                            growXXS: 12,
                            growMD: 3,
                          }}
                        />
                        <Field.TextInput
                          object={tool}
                          field={"description"}
                          label="Description"
                          wrapperProps={{
                            growXXS: 12,
                            growMD: 3,
                          }}
                        />
                        <Field.TextInput
                          object={tool}
                          field={"serial_number"}
                          label="Serial Number"
                          wrapperProps={{
                            growXXS: 12,
                            growMD: 3,
                          }}
                        />
                        <Field.TextInput
                          prefix="$"
                          object={tool}
                          field={"limit"}
                          label="Limit"
                          isFormattedNumber
                          wrapperProps={{
                            growXXS: 12,
                            growMD: 3,
                          }}
                        />
                      </GridContainer>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Box />
                        <ActionButton.Delete
                          objectName="tool_equipment"
                          objectId={tool.id}
                          background
                        >
                          Remove {tool.description}
                        </ActionButton.Delete>
                      </Box>
                    </LocationDiv>
                  )
                )}
                <ActionButton.Add
                  params={{
                    cim_contractors_tool_equipment_id:
                      skp.cim_contractors_tool_equipment.id,
                  }}
                  objectName="tool_equipment"
                  background
                >
                  Tool or Equipment
                </ActionButton.Add>
              </LocationDiv>
            )}
          </LocationDiv>
        </>
      )}
      <LocationDiv>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={20}
          my={20}
        >
          <Field.CheckBox
            label="Installation Floater"
            object={skp}
            field={"installation_floater_coverage_flag"}
            wrapperProps={{
              grow: 12,
            }}
          />
        </Box>
        {skp.installation_floater_coverage_flag && (
          <IndentWrapper>
            <Field.DropDown
              options={instalationFloaterLimits.map((item) => ({
                title: formatAmount(item),
                value: parseInt(item),
              }))}
              object={skp}
              field={"installation_floater_limit"}
              label="At any one Installation premises Limit"
            />
          </IndentWrapper>
        )}
      </LocationDiv>
      {locations.map((location, index) => (
        <LocationDiv>
          <FormTitle.SubHeader>{location.line_1}</FormTitle.SubHeader>
          <SkpPersonalPropertyForm
            locationId={location.id}
            buildings={location.buildings}
          />
        </LocationDiv>
      ))}
    </>
  );
}

export default SkpCoverageForm;
